import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import BASE_QUERY from '@/features/baseQuery';
import { dealsApi } from '../deals/dealsApi';
import {
	addDoc,
	query,
	where,
	collection,
	doc,
	Timestamp,
	updateDoc,
	getDocs,
} from 'firebase/firestore';
import { firestore } from '@/utils/firebase';
import { getActingUser } from '../user/userApi';
import { safelyParseDate } from '@/utils/date';

const GENERATED_PROPOSALS_COLLECTION = 'generatedProposals';
const GENERATED_SOA_COLLECTION = 'generatedSOA';

const asMillis = (value) => {
	const parsed = safelyParseDate(value);
	return parsed?.getTime?.() ?? 0;
};

const getDocCreatedWhenMillis = (doc) =>
	asMillis(doc?.createdWhen ?? doc?.lastUpdated ?? doc?.dateCreated ?? doc?.created_at);

export const AI_SUMMARY_TYPES = {
	wytu: 'wytu',
	quote: 'quote',
	summary: 'summary',
	riskInfoSummary: 'riskInfoSummary',
	riskInfoEmail: 'riskInfoEmail',
	emailTemplate: 'emailTemplate',
	quoteSummary: 'quoteSummary',
	quoteRecommendationEmail: 'quoteRecommendationEmail',
	renewalTermsEmail: 'renewalTermsEmail',
	unlayerEmailJson: 'unlayerEmailJson',
	fileExtract: 'fileExtract',
	createVectorStore: 'createVectorStore',
	unlayerAIEmailJsonTemplate: 'unlayerAIEmailJsonTemplate',
	riskInfoSummaryJson: 'riskInfoSummaryJson',
	unlayerEmailFromMarkdown: 'unlayerEmailFromMarkdown',
};

const filterArray = (array = [], type) => {
	const docTypes = ['wytu', 'riskInfo'].includes(type) ? ['wytu', 'riskInfo'] : [type];
	const data = array.filter((e) => docTypes.includes(e.type));
	return data.sort((a, b) => getDocCreatedWhenMillis(b) - getDocCreatedWhenMillis(a));
};

// Creating an API service using RTK Query
export const factFindApi = createApi({
	reducerPath: 'factFindApi',
	tagTypes: ['FACT_FIND', 'DEAL_NOTE_SUMMARY', 'GENERATED_SOA'],
	baseQuery: fetchBaseQuery(BASE_QUERY),
	endpoints: (builder) => ({
		getDealNoteSummaries: builder.query({
			providesTags: (result, error, { dealId }) => {
				return [{ type: 'DEAL_NOTE_SUMMARY', id: dealId }];
			},
			query: ({ dealId, type }) => ({
				url: process.env.REACT_APP_RETRIEVE_DEAL_NOTE_SUMMARIES,
				method: 'POST',
				body: { dealId, type },
			}),
			transformResponse: (response) => {
				const getMs = (item) => {
					return asMillis(item?.dateCreated ?? item?.created_at);
				};
				console.log('👾 ~ transform getDealNoteSummaries response', response);
				const sorted = Object.entries(response).reduce((acc, res) => {
					const [key, array] = res;
					acc[key] = array
						.map((i) => ({
							...i,
							message: i?.content?.[0]?.text?.value ?? i?.message ?? '',
							role: i?.role ?? 'assistant',
							createdWhen: getMs(i),
						}))
						.sort((a, b) => b.createdWhen - a.createdWhen);
					return acc;
				}, {});

				return sorted;
			},
			transformErrorResponse: (response) => {
				console.log('👾 ~ transform getDealNoteSummaries error response', response);
				return response;
			},
		}),
		updateDealNoteSummary: builder.mutation({
			invalidatesTags: (result, error, arg) => [
				{ type: 'DEAL_NOTE_SUMMARY', id: arg.dealId },
			],
			query: ({ dealId, content }) => ({
				url: process.env.REACT_APP_ADD_NOTE_SUMMARY,
				method: 'POST',
				body: { dealId, content },
			}),
			transformResponse: (response) => {
				console.log('👾 ~ transform updateDealNoteSummary response', response);
				return response;
			},
			onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
				try {
					await queryFulfilled;
					dispatch(dealsApi.util.invalidateTags([{ type: 'DEAL_ROW', id: arg.dealId }]));
				} catch (error) {
					console.log('Error invalidating tags', error);
				}
			},
		}),
		//Returns Summarys wytu and Quote later on
		getAIJsonDocs: builder.query({
			providesTags: (result, error, { dealId }) => {
				return [{ type: 'DEAL_NOTE_SUMMARY', id: dealId }];
			},
			query: ({ dealId, type }) => ({
				url: process.env.REACT_APP_RETRIEVE_SUMMARIES,
				method: 'POST',
				body: { dealId, type },
			}),
			transformResponse: (response) => {
				console.log('👾 ~ transform getAIJsonDocs response', response);
				const quotes = filterArray(response, 'quote');
				const riskInfo = filterArray(response, 'riskInfo');
				return { quotes, riskInfo };
			},
			transformErrorResponse: (response) => {
				console.log('👾 ~ transform getAIJsonDocs error response', response);
				return response;
			},
		}),
		createDealProposal: builder.mutation({
			invalidatesTags: (result, error, { dealId }) => [
				{ type: 'DEAL_NOTE_SUMMARY', id: dealId },
			],
			queryFn: async (
				{ dealId, threadId, jsonData, type, docId, generating, emailUser = false },
				{ dispatch, getState },
			) => {
				const userData = await getActingUser(dispatch, getState);

				try {
					let docRef;
					if (docId) {
						docRef = doc(firestore, GENERATED_PROPOSALS_COLLECTION, docId);
					}

					if (docRef) {
						return await updateDoc(docRef, {
							jsonData,
							lastUpdated: Timestamp.now(),
							aiGenerated: false,
							createdBy: userData?.userId,
						}).then((res) => {
							console.log(`Successfully updated proposal doc ${docId}`, res);
							return {
								data: res?.id,
							};
						});
					} else {
						return await addDoc(collection(firestore, GENERATED_PROPOSALS_COLLECTION), {
							lastUpdated: Timestamp.now(),
							aiGenerated: false,
							dealId,
							type,
							createdWhen: Timestamp.now(),
							...(jsonData && { jsonData }),
							...(threadId && { threadId }),
							...(generating && { generating }),
							createdBy: userData?.userId,
							emailUser,
						}).then((res) => {
							console.log(`Successfully created proposal doc ${res.id}`, res);
							return {
								data: res?.id,
							};
						});
					}
				} catch (error) {
					console.log('Error updating Firebase document', error);
					return {
						error,
					};
				}
			},
			transformResponse: (response) => {
				console.log('👾 ~ transform create deal Proposal response', response);
				return response;
			},
			onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
				try {
					await queryFulfilled;
					dispatch(
						dealsApi.util.invalidateTags([
							{ type: 'DEAL_ROW', id: arg.dealId },
							{ type: 'DEAL', id: arg.dealId },
						]),
					);
				} catch (error) {
					console.log('Error invalidating tags', error);
				}
			},
		}),
		getLatestDealSOAs: builder.query({
			providesTags: (result, error, { dealId }) => {
				return [{ type: 'GENERATED_SOA', id: dealId }];
			},
			queryFn: async ({ dealId }) => {
				const soaRef = collection(firestore, GENERATED_SOA_COLLECTION);

				const soaQuery = query(soaRef, where('dealId', 'in', [dealId, dealId.toString()]));

				const querySnapshot = await getDocs(soaQuery);
				const docs = querySnapshot.docs.map((doc) => ({
					id: doc.id,
					...doc.data(),
				}));

				const sortedData = docs.sort((a, b) => {
					if (a.createdWhen.seconds !== b.createdWhen.seconds) {
						return b.createdWhen.seconds - a.createdWhen.seconds;
					}
					return b.createdWhen.nanoseconds - a.createdWhen.nanoseconds;
				});
				return {
					data: sortedData.map((doc) => ({
						...(doc ? JSON.parse(JSON.stringify(doc)) : {}),
						quote: doc.quote?.id,
						riskInfo: doc.riskInfo?.id,
						createdWhen: doc.createdWhen.toMillis(),
					})),
				};
			},
		}),
		generateSOA: builder.mutation({
			invalidatesTags: (result, error, { dealId }) => [{ type: 'GENERATED_SOA', id: dealId }],
			queryFn: async ({ dealId, userId, quoteId, riskInfoId, formJson }) => {
				const quote = quoteId
					? doc(firestore, GENERATED_PROPOSALS_COLLECTION, quoteId)
					: null;
				const riskInfo = riskInfoId
					? doc(firestore, GENERATED_PROPOSALS_COLLECTION, riskInfoId)
					: null;
				if (formJson) {
					console.log('Create new SOA with the following information', {
						dealId,
						userId,
						generating: true,
						createdWhen: Timestamp.now(),
						formJson,
						...(quote && { quote }),
						...(riskInfo && { riskInfo }),
					});
					return await addDoc(collection(firestore, GENERATED_SOA_COLLECTION), {
						dealId,
						userId,
						generating: true,
						createdWhen: Timestamp.now(),
						formJson,
						...(quote && { quote }),
						...(riskInfo && { riskInfo }),
					})
						.then((res) => {
							console.log(`Successfully created generated SOA doc ${res.id}`, res);
							return {
								data: res?.id,
							};
						})
						.catch((err) => {
							console.log('Error creating Firebase document', err);
							return {
								error: {
									status: 500,
									data: err.message || 'Unknown error occurred',
								},
							};
						});
				} else {
					console.log(
						`Error: could not find quote ${quoteId} and riskInfo ${riskInfoId} documents`,
					);
					return {
						error: {
							status: 400,
							data: `Error: could not find quote ${quoteId} and riskInfo ${riskInfoId} documents`,
						},
					};
				}
			},
			transformResponse: (response) => {
				console.log('👾 ~ transform create deal Proposal response', response);
				return response;
			},
		}),
	}),
});

// Exporting auto-generated hooks for the defined endpoints
export const {
	useGetDealNoteSummariesQuery,
	useUpdateDealNoteSummaryMutation,
	useGetAIJsonDocsQuery,
	useGetLatestDealSOAsQuery,
	useCreateDealProposalMutation,
	useGenerateSOAMutation,
} = factFindApi;
